import * as d3 from 'd3-selection'
import keycodes from '../../keycodes.js'
import * as interactions from '../interactions.js'
import buildCreateable from '../../build-creatable.js'

export default function (selection, selectedItems) {
  selection.each(function (data) {
    var input = d3.select(this)
                  .selectAll('textarea') // the input is a textarea so someone can paste new lines and we break them into separate tags
                  .data(function (d) {
                    return data.disableKeyboardControl ? [] : [d]
                  })

    input.enter()
         .append('textarea')
           .attr('rows', 1)
           .attr('autocomplete', 'off')
           .attr('spellcheck', 'false')
         .merge(input)
         .classed('has-selected-items', !!selectedItems.length)
         .select(function () {
           this._blurred = false
           return this
         })
         .attr('placeholder', function (d) {
           if (!d.filterable) {
             // if it's not a filterable select, there isn't a placeholder
             return undefined
           }
           var selected = d.options.filter(function (d) {
                                     return d.selected
                                   })

           return (!selected.length || d.hideSelectedOptions) && d.placeholder ? d.placeholder : ''
         })
         .attr('disabled', function (d) {
           return d.disabled || null
         })
         .classed('combo-input', function (d) {
           return d.combo
         })
         .classed('has-placeholder', function () {
           return !!d3.select(this).attr('placeholder')
         })
         .attr('readonly', function (d) {
           return d.filterable ? null : 'readonly'
         })
         .on('focus.select', function (e, d) {
           d.textareaFocused = true
           if (!data.open) {
             // Only trigger the redraw if the menu is closed
             data.emit('change:open')
           }
         })
         .on('blur.select', function (e, d) {
            // Note! Don't close data.open because we may want the menu to stay open (e.g. mobile safari when the menu is open and someone wants to hide the keyboard)
           d.textareaFocused = false
           this._blurred = true

           if (d.open && d.changeableTags) {
             // The menu is open and this select allows users to change the selected tag
             // See if there are any active tags
             var active = d.options.filter(function (d) { return d.active }).length
             if (active) {
               // There's an active tag, don't do anything. The user is allowed to change it
               return
             }
           }

           if (d.multiple) {
             d.options.forEach(function (opt) {
               opt.active = false
             })
           }
           data.emit('change:open')
         })
         .on('paste', function (e, d) {
           // If it's a multiple select, see if we should split the pasted text into new tags based on the `splitOn` delimiter
           if (d.multiple) {
             // Wait for the value to be populated
             process.nextTick(function () {
               var pasted = this.value
                 , split = pasted.split(d.splitOn)

               if (split.length > 1) {
                 var options = split.map(function (token) {
                                      // See if this token is part of our options
                                      var opt = d.options.filter(function (o) {
                                                            return o[d.accessors.label] === token // Match the labels
                                                          })[0]
                                      if (!opt) {
                                        // Not in the list so create this new option
                                        opt = buildCreateable(token, d)
                                      }
                                      return opt
                                    }).filter(Boolean)
                  interactions.select(data, options)

               }
             }.bind(this))
           }
         })
         .on('keyup.select', function (e, d) {
           if (d._keydownClose) {
             // If the user just closed an open menu with escape, we want to stopPropagation
             e.stopPropagation()
             // And then clean up after ourselves
             delete d._keydownClose
           }

           if (!d.filterable) {
             return
           }

           var value = this.value
           if (d.searchTerm === undefined && !value) {
             return
           }

           if (d.searchTerm !== value) {
             d._refreshOptions = true
             d.open = true
             d.searchTerm = value
             data.emit('change:search-term')
           }
         })
         .on('keydown.select', function (e, d) {
           var highlighted = d.menuOptions.filter(function (d) {
                                          return d.highlighted
                                        })[0]
             , selected = selectedItems[selectedItems.length - 1]

           switch (e.keyCode) {
             case keycodes.SPACE:
               if (!data.open) {
                 interactions.open(data)
                 e.preventDefault()
               } else if (!data.filterable) {
                 interactions.select(data, highlighted)
                 e.preventDefault()
               } else if (selected && !d.multiple) {
                 e.preventDefault()
               }

               return
             case keycodes.ESCAPE:
               if (d.open) {
                 // Set a flag that it was open, but we're closing it,
                 // so the keyup event can determine if we should allow event propagation
                 d._keydownClose = true
               }
               interactions.close(data)
               return
             case keycodes.TAB:
               // Close the menu because they are tabbing to a new control
               interactions.close(data)
               return
             case keycodes.SHIFT:
               return
             case keycodes.LEFT:
             case keycodes.RIGHT:
               interactions.activateTag(data, e.keyCode === keycodes.LEFT ? -1 : 1, selectedItems)
               if (e.metaKey && data.open) {
                 e.preventDefault()
               }
               return
             case keycodes.DOWN:
             case keycodes.UP:
               if (!data.open) {
                 interactions.open(data)
                 e.preventDefault()
                 return
               }
               var idx = -1
                 , direction = e.keyCode === keycodes.UP ? -1 : 1

               if (data.multiple) {
                 if (highlighted) {
                   idx = d.menuOptions.indexOf(highlighted)
                   idx += direction
                 } else {
                   idx = 0 // start at 0
                 }
               } else {
                 idx = d.menuOptions.indexOf(highlighted || selected)
                 idx += direction
               }

               if (e.metaKey) {
                 idx = direction > 0 ? d.menuOptions.length - 1 : 0
               }

               var opt = (function getOpt (idx, direction) {
                if (d.menuOptions[idx] && d.menuOptions[idx].disabled) {
                  return getOpt(idx + direction, direction)
                } else {
                  return d.menuOptions[idx]
                }
               })(idx, direction)

               if (opt) {
                 highlighted && delete highlighted.highlighted
                 opt.highlighted = true
                 data.emit('change:highlighted', opt)
               }
               e.preventDefault()
               return
             case keycodes.BACKSPACE:
             case keycodes.DELETE:
               interactions.remove(data, selected, selectedItems)
               return
             case keycodes.ENTER:
               interactions.select(data, highlighted)
               e.preventDefault()
               return
           }

           if (e.ctrlKey || e.metaKey || e.altKey) {
             // Don't worry about clearing it since the user hit a non-character key
             return
           }

           // If we have a selected option on a filtering select, clear it
           if (!data.multiple && selected) {
             interactions.remove(data, selected, selectedItems)
           }
         })
         .call(function (selection) {
           selection.each(function () {
             if (this._blurred) {
               // A blur event just fired, which means the textarea lost focus, so we don't want to automatically focus again
               // This allows the keyboard to close on ios.
               delete this._blurred
               return
             }
             if (data.open && !data.textareaFocused) {
               this.focus()
             }
           })
         })
         .select(function (d) {
           if (d.combo) {
             var selected = data.options.filter(function (s) {
                                          return s.selected
                                        })[0]
                , value = d.searchTerm || ''

             if (selected && (typeof selected[d.accessors.value] !== 'undefined' || selected[d.accessors.value] !== null)) {
               value = selected[d.accessors.value]
             }

             // Set the value to the selected option's `.value`, rather than `.label`. The label
             // could be escaped
             this.value = value

             // Set the searchTerm to the current value for future filtering
             d.searchTerm = this.value
           }

           if ((!d.open && !d.combo) || d._clearInput) {
             delete d._clearInput
             // if we are closed and not a combo box, the textarea box should be empty
             // since it's not shown (div.items are)
             // Clear the textarea if it's not a combo box
             this.value = ''
             delete d.searchTerm
           }
         })
  })
}
